import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Send, MessageCircle, User, Search, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  sub_agent: 'Sub-Agente',
  interno: 'Interno',
  back_office: 'Back Office'
};

const ROLE_COLORS = {
  super_admin: 'bg-violet-100 text-violet-700',
  admin: 'bg-sky-100 text-sky-700',
  sub_agent: 'bg-emerald-100 text-emerald-700',
  interno: 'bg-amber-100 text-amber-700',
  back_office: 'bg-slate-100 text-slate-700'
};

const Chat = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { fetchContacts(); }, []);

  useEffect(() => {
    if (selectedContact) {
      fetchMessages(selectedContact.id);
      pollRef.current = setInterval(() => fetchMessages(selectedContact.id), 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [selectedContact?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchContacts = async () => {
    try { const res = await axios.get(`${API_URL}/api/chat/contacts`); setContacts(res.data); }
    catch (e) { toast.error('Errore caricamento contatti'); }
  };

  const fetchMessages = async (contactId) => {
    try { const res = await axios.get(`${API_URL}/api/chat/messages/${contactId}`); setMessages(res.data); }
    catch (e) {}
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedContact || sending) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/chat/messages/${selectedContact.id}`, { contenuto: newMessage.trim() });
      setNewMessage('');
      fetchMessages(selectedContact.id);
      fetchContacts();
    } catch (e) {
      toast.error('Errore invio messaggio');
    } finally { setSending(false); }
  };

  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setMobileShowChat(true);
  };

  const handleBackToContacts = () => {
    setMobileShowChat(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const formatTime = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 86400000) return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    if (diff < 172800000) return 'Ieri';
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  const filteredContacts = contacts.filter(c =>
    !searchQuery || `${c.nome} ${c.cognome}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const ContactsList = () => (
    <div className={`${mobileShowChat ? 'hidden md:flex' : 'flex'} w-full md:w-80 bg-white border-r border-slate-200 flex-col`}>
      <div className="p-4 border-b border-slate-100">
        <h2 className="text-lg font-bold text-slate-900 mb-3">Chat</h2>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca contatto..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9 text-sm" data-testid="chat-search" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filteredContacts.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Nessun contatto</p>
        ) : (
          filteredContacts.map(contact => (
            <div key={contact.id} onClick={() => handleSelectContact(contact)}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-slate-50 transition-colors ${
                selectedContact?.id === contact.id ? 'bg-sky-50 border-l-2 border-l-sky-500' : 'hover:bg-slate-50'
              }`} data-testid={`chat-contact-${contact.id}`}>
              <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-900 truncate">{contact.nome} {contact.cognome}</p>
                  {contact.non_letti > 0 && (
                    <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 text-white text-xs font-bold">
                      {contact.non_letti}
                    </span>
                  )}
                </div>
                <Badge className={`text-[10px] px-1.5 py-0 ${ROLE_COLORS[contact.ruolo] || ''}`}>
                  {ROLE_LABELS[contact.ruolo] || contact.ruolo}
                </Badge>
                {contact.ultimo_messaggio && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">{contact.ultimo_messaggio}</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const ChatArea = () => (
    <div className={`${!mobileShowChat ? 'hidden md:flex' : 'flex'} flex-1 flex-col bg-slate-50`}>
      {!selectedContact ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Seleziona un contatto per iniziare</p>
          </div>
        </div>
      ) : (
        <>
          <div className="px-3 sm:px-5 py-3 bg-white border-b border-slate-200 flex items-center gap-3">
            <button onClick={handleBackToContacts} className="md:hidden p-1 -ml-1 text-slate-500 hover:text-slate-900" data-testid="chat-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-slate-500" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 truncate">{selectedContact.nome} {selectedContact.cognome}</p>
              <Badge className={`text-[10px] px-1.5 py-0 ${ROLE_COLORS[selectedContact.ruolo] || ''}`}>
                {ROLE_LABELS[selectedContact.ruolo] || selectedContact.ruolo}
              </Badge>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 space-y-3">
            {messages.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-8">Nessun messaggio. Scrivi il primo!</p>
            ) : (
              messages.map(msg => {
                const isMine = msg.sender_id === user?.id;
                return (
                  <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 ${
                      isMine ? 'bg-sky-500 text-white rounded-br-md' : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md'
                    }`}>
                      {!isMine && <p className="text-[10px] font-semibold text-sky-600 mb-0.5">{msg.sender_nome}</p>}
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.contenuto}</p>
                      <p className={`text-[10px] mt-1 ${isMine ? 'text-sky-100' : 'text-slate-400'} text-right`}>
                        {formatTime(msg.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="px-3 sm:px-5 py-3 bg-white border-t border-slate-200">
            <div className="flex gap-2">
              <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={handleKeyPress}
                placeholder="Scrivi un messaggio..." className="flex-1" data-testid="chat-input" />
              <Button onClick={handleSend} disabled={!newMessage.trim() || sending}
                className="bg-sky-500 hover:bg-sky-600 shrink-0" data-testid="chat-send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-120px)] gap-0 rounded-xl overflow-hidden border border-slate-200" data-testid="chat-page">
      <ContactsList />
      <ChatArea />
    </div>
  );
};

export default Chat;
